import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
const api = require("../../api/secureMessageApi");
const handler = require("../../utils/secure/messageHandler");
const socketClient = require("../../api/secureSocket");
const keyManager = require("../../utils/secure/keyManager");

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function TestSecureMessaging() {
  const q = useQuery();
  const [manualToken, setManualToken] = useState("");
  const [otherUserId, setOtherUserId] = useState(q.get("otherUserId") || "");
  const [chatId, setChatId] = useState(q.get("chatId") || "");
  const [plaintext, setPlaintext] = useState("");
  const [messages, setMessages] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const unsubRef = useRef(null);

  useEffect(() => {
    // connect socket
    socketClient.connect();
    return () => {
      try {
        // Clean up all listeners
        unsubRef.current && unsubRef.current();
        socketClient.off("secure_message");
        socketClient.off("secure_typing");
        socketClient.off("secure_messages_read");
      } catch (_e) {}
      socketClient.disconnect();
    };
  }, []);

  const setTokenHeader = useCallback(() => {
    try {
      if (!manualToken) return;
      // Backend expects 'token' header; our client reads from localStorage
      localStorage.setItem("token", manualToken);
      localStorage.setItem("loginDetails", manualToken);
      console.log("[TEST] token set in localStorage");
    } catch (e) {
      console.error("[TEST] set token error:", e);
    }
  }, [manualToken]);

  const initMyKeys = useCallback(async () => {
    try {
      const res = await api.initializeUserKeys();
      console.log("[TEST] initializeUserKeys (current user):", res);
    } catch (e) {
      // If already initialized, backend returns 400; that's fine for test
      console.warn("[TEST] initializeUserKeys warning:", e?.message || e);
    }
  }, []);

  const addTestResult = useCallback((test, status, details) => {
    const result = {
      id: Date.now(),
      test,
      status,
      details,
      timestamp: new Date().toISOString(),
    };
    setTestResults((prev) => [result, ...prev.slice(0, 9)]); // Keep last 10 results
    console.log(`🧪 [TEST] ${test}: ${status}`, details);
  }, []);

  const createChat = useCallback(async () => {
    if (!otherUserId) {
      addTestResult("Create Chat", "FAILED", "Other user ID is required");
      return;
    }
    try {
      addTestResult("Create Chat", "STARTING", "Initializing user keys...");

      // Ensure current user's keys are initialized first
      try {
        await api.initializeUserKeys();
        addTestResult(
          "Initialize Keys",
          "SUCCESS",
          "Current user keys initialized"
        );
      } catch (e) {
        addTestResult(
          "Initialize Keys",
          "WARNING",
          e?.message || "Keys may already exist"
        );
      }

      addTestResult(
        "Create Chat",
        "IN_PROGRESS",
        "Creating chat with other user..."
      );
      const res = await api.createChat(otherUserId);
      const created = res?.data || res;

      if (created?._id) {
        setChatId(created._id);
        addTestResult(
          "Create Chat",
          "SUCCESS",
          `Chat created with ID: ${created._id}`
        );

        // join socket room
        socketClient.joinSecureChat(created._id);
        addTestResult(
          "Socket Join",
          "SUCCESS",
          `Joined secure room: ${created._id}`
        );

        // subscribe for messages
        unsubRef.current = socketClient.onSecureMessage((payload) => {
          addTestResult(
            "Socket Message",
            "RECEIVED",
            `Message from ${payload?.sender || "unknown"}`
          );
        });

        // subscribe for typing events
        socketClient.onTyping((payload) => {
          addTestResult(
            "Typing Event",
            "RECEIVED",
            `User ${payload?.userId || "unknown"} is ${
              payload?.isTyping ? "typing" : "not typing"
            }`
          );
        });

        // subscribe for read receipt events
        socketClient.onMessagesRead((payload) => {
          addTestResult(
            "Read Receipt",
            "RECEIVED",
            `User ${payload?.userId || "unknown"} marked messages as read`
          );
        });

        // Prime/verify session key fetch for this chat
        try {
          const key = await keyManager.getSessionKey(created._id);
          addTestResult(
            "Session Key",
            "SUCCESS",
            "Session key retrieved and cached"
          );
        } catch (e) {
          addTestResult(
            "Session Key",
            "WARNING",
            "Other user may not have keys initialized"
          );
        }
      } else {
        addTestResult("Create Chat", "FAILED", "No chat ID returned from API");
      }
    } catch (e) {
      addTestResult("Create Chat", "FAILED", e?.message || "Unknown error");
      if (String(e?.message || "").includes("encryption keys")) {
        addTestResult(
          "Create Chat",
          "HINT",
          "Other user needs to initialize keys first"
        );
      }
    }
  }, [otherUserId, addTestResult]);

  const joinRoom = useCallback(() => {
    if (!chatId) return;
    socketClient.joinSecureChat(chatId);

    // subscribe for messages
    unsubRef.current = socketClient.onSecureMessage((payload) => {
      addTestResult(
        "Socket Message",
        "RECEIVED",
        `Message from ${payload?.sender || "unknown"}`
      );
    });

    // subscribe for typing events
    socketClient.onTyping((payload) => {
      addTestResult(
        "Typing Event",
        "RECEIVED",
        `User ${payload?.userId || "unknown"} is ${
          payload?.isTyping ? "typing" : "not typing"
        }`
      );
    });

    // subscribe for read receipt events
    socketClient.onMessagesRead((payload) => {
      addTestResult(
        "Read Receipt",
        "RECEIVED",
        `User ${payload?.userId || "unknown"} marked messages as read`
      );
    });
  }, [chatId, addTestResult]);

  const sendEncrypted = useCallback(async () => {
    if (!chatId || !plaintext) {
      addTestResult("Send Message", "FAILED", "Missing chatId or plaintext");
      return;
    }

    try {
      addTestResult("Send Message", "STARTING", `Sending: "${plaintext}"`);
      const created = await handler.sendMessage(chatId, plaintext);
      addTestResult(
        "Send Message",
        "SUCCESS",
        `Message sent with ID: ${created?._id || "unknown"}`
      );
      setPlaintext(""); // Clear input after successful send
    } catch (e) {
      addTestResult("Send Message", "FAILED", e?.message || "Unknown error");
    }
  }, [chatId, plaintext, addTestResult]);

  const testTyping = useCallback(() => {
    if (!chatId) {
      addTestResult("Typing Test", "FAILED", "No chat ID available");
      return;
    }

    const userId = "test-user-" + Date.now();
    setIsTyping(true);
    socketClient.emitTyping(chatId, userId, true);
    addTestResult("Typing Test", "SENT", "Typing started");

    setTimeout(() => {
      socketClient.emitTyping(chatId, userId, false);
      setIsTyping(false);
      addTestResult("Typing Test", "SENT", "Typing stopped");
    }, 2000);
  }, [chatId, addTestResult]);

  const testMessagesRead = useCallback(() => {
    if (!chatId) {
      addTestResult("Read Receipt Test", "FAILED", "No chat ID available");
      return;
    }

    const userId = "test-user-" + Date.now();
    socketClient.emitMessagesRead(chatId, userId);
    addTestResult(
      "Read Receipt Test",
      "SENT",
      `Marked messages as read for user: ${userId}`
    );
  }, [chatId, addTestResult]);

  const simulateOtherUserEvents = useCallback(() => {
    if (!chatId) {
      addTestResult("Simulate Events", "FAILED", "No chat ID available");
      return;
    }

    const otherUserId = "simulated-user-" + Date.now();

    // Simulate typing start
    socketClient.emitTyping(chatId, otherUserId, true);
    addTestResult(
      "Simulate Events",
      "SENT",
      "Simulated typing start from other user"
    );

    // Simulate typing stop after 1 second
    setTimeout(() => {
      socketClient.emitTyping(chatId, otherUserId, false);
      addTestResult(
        "Simulate Events",
        "SENT",
        "Simulated typing stop from other user"
      );
    }, 1000);

    // Simulate read receipt after 2 seconds
    setTimeout(() => {
      socketClient.emitMessagesRead(chatId, otherUserId);
      addTestResult(
        "Simulate Events",
        "SENT",
        "Simulated read receipt from other user"
      );
    }, 2000);
  }, [chatId, addTestResult]);

  const fetchMessages = useCallback(async () => {
    if (!chatId) return;
    try {
      const res = await api.getMessages(chatId);
      const list = res?.data || res || [];
      console.log("[TEST] getMessages (encrypted):", list);
      setMessages(list);
    } catch (e) {
      console.error("[TEST] getMessages error:", e);
    }
  }, [chatId]);

  const decryptLatest = useCallback(async () => {
    try {
      const dec = await handler.decryptMessages(messages, chatId);
      console.log("[TEST] decryptMessages:", dec);
    } catch (e) {
      console.error("[TEST] decryptMessages error:", e);
    }
  }, [messages, chatId]);

  return (
    <div style={{ padding: 16 }}>
      <h2>Secure Messaging Test</h2>
      <div style={{ marginBottom: 8 }}>
        <label>Token:&nbsp;</label>
        <input
          value={manualToken}
          onChange={(e) => setManualToken(e.target.value)}
          placeholder="paste token if not logged in"
          style={{ width: 360 }}
        />
        <button onClick={setTokenHeader} style={{ marginLeft: 8 }}>
          Set Token
        </button>
      </div>
      <div style={{ marginBottom: 8 }}>
        <button onClick={initMyKeys}>Initialize My Keys</button>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label>Other User ID:&nbsp;</label>
        <input
          value={otherUserId}
          onChange={(e) => setOtherUserId(e.target.value)}
          placeholder="target userId"
        />
        <button onClick={createChat} style={{ marginLeft: 8 }}>
          Create 1:1 Chat
        </button>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label>Chat ID:&nbsp;</label>
        <input
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder="chatId"
        />
        <button onClick={joinRoom} style={{ marginLeft: 8 }}>
          Join Room
        </button>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label>Plaintext:&nbsp;</label>
        <input
          value={plaintext}
          onChange={(e) => setPlaintext(e.target.value)}
          placeholder="message"
        />
        <button onClick={sendEncrypted} style={{ marginLeft: 8 }}>
          Send Encrypted
        </button>
      </div>
      <div style={{ marginBottom: 8 }}>
        <button onClick={fetchMessages}>Fetch Encrypted Messages</button>
        <button onClick={decryptLatest} style={{ marginLeft: 8 }}>
          Decrypt Fetched
        </button>
      </div>

      <div
        style={{
          marginBottom: 8,
          border: "1px solid #ccc",
          padding: "10px",
          borderRadius: "4px",
        }}
      >
        <h3>Socket Testing</h3>
        <button
          onClick={testTyping}
          disabled={!chatId || isTyping}
          style={{ marginRight: 8 }}
        >
          {isTyping ? "Typing..." : "Test Typing"}
        </button>
        <button
          onClick={testMessagesRead}
          disabled={!chatId}
          style={{ marginRight: 8 }}
        >
          Test Read Receipt
        </button>
        <button
          onClick={simulateOtherUserEvents}
          disabled={!chatId}
          style={{ marginRight: 8 }}
        >
          Simulate Other User
        </button>
        <button onClick={() => setTestResults([])} style={{ marginRight: 8 }}>
          Clear Results
        </button>
      </div>

      <div
        style={{
          marginBottom: 8,
          border: "1px solid #ccc",
          padding: "10px",
          borderRadius: "4px",
        }}
      >
        <h3>Test Results</h3>
        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {testResults.length === 0 ? (
            <p style={{ color: "#666" }}>
              No test results yet. Start testing to see results here.
            </p>
          ) : (
            testResults.map((result) => (
              <div
                key={result.id}
                style={{
                  marginBottom: "4px",
                  padding: "4px",
                  backgroundColor:
                    result.status === "SUCCESS"
                      ? "#d4edda"
                      : result.status === "FAILED"
                      ? "#f8d7da"
                      : result.status === "WARNING"
                      ? "#fff3cd"
                      : "#e2e3e5",
                  borderRadius: "3px",
                  fontSize: "12px",
                }}
              >
                <strong>{result.test}</strong> -{" "}
                <span
                  style={{
                    color:
                      result.status === "SUCCESS"
                        ? "green"
                        : result.status === "FAILED"
                        ? "red"
                        : result.status === "WARNING"
                        ? "orange"
                        : "blue",
                  }}
                >
                  {result.status}
                </span>
                <br />
                <span style={{ color: "#666" }}>{result.details}</span>
                <br />
                <span style={{ color: "#999", fontSize: "10px" }}>
                  {result.timestamp}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <p>Open console to see detailed logs.</p>
    </div>
  );
}
